import { useEffect, useRef, useCallback, useMemo } from "react";
import { useTheme } from "@mui/material";
import mapboxgl from "mapbox-gl";
import { CoamAreaFeature } from "../api/coamArea";

const SOURCE_ID = "coam-areas";
const LABEL_SOURCE_ID = "coam-areas-labels-source";
const LAYER_ID = "coam-areas-fill";
const OUTLINE_LAYER_ID = "coam-areas-outline";
const LABEL_LAYER_ID = "coam-areas-labels";
const ANIMATION_DURATION = 1000;
const PANEL_WIDTH_OFFSET = 300;
const UK_CENTER: [number, number] = [-1.895662, 52.489706];
const INITIAL_ZOOM = 6;
const PADDING = { top: 50, bottom: 50, left: 50, right: 350 };

interface UseCoamAreasLayerOptions {
  map: mapboxgl.Map | null;
  features: CoamAreaFeature[];
  selectedFeature: CoamAreaFeature | null;
  setSelectedFeature: (feature: CoamAreaFeature | null) => void;
}

export const useCoamAreasLayer = ({
  map,
  features,
  selectedFeature,
  setSelectedFeature,
}: UseCoamAreasLayerOptions) => {
  const theme = useTheme();
  const selectedPolygonIdRef = useRef<string | null>(null);
  const hoveredPolygonIdRef = useRef<string | null>(null);
  const eventHandlersRef = useRef<{
    handleMouseMove: ((e: mapboxgl.MapLayerMouseEvent) => void) | null;
    handleMouseLeave: (() => void) | null;
    handleClick: ((e: mapboxgl.MapLayerMouseEvent) => void) | null;
  }>({
    handleMouseMove: null,
    handleMouseLeave: null,
    handleClick: null,
  });

  const COLORS = useMemo(
    () => ({
      default: {
        stroke: theme.palette.primary.light,
        fill: theme.palette.primary.light,
        fillOpacity: 0.5,
      },
      hover: {
        stroke: theme.palette.primary.main,
        fill: theme.palette.primary.main,
        fillOpacity: 0.5,
      },
      selected: {
        stroke: theme.palette.primary.dark,
        fill: theme.palette.primary.dark,
        fillOpacity: 0.5,
      },
    }),
    [theme.palette.primary]
  );

  const createFeatureCollection = useCallback(
    (geoFeatures: CoamAreaFeature[]) => {
      return {
        type: "FeatureCollection" as const,
        features: geoFeatures.map((feature) => ({
          ...feature,
          id: feature.properties.id.toString(),
        })),
      };
    },
    []
  );

  const createLabelFeatureCollection = useCallback(
    (geoFeatures: CoamAreaFeature[]) => {
      return {
        type: "FeatureCollection" as const,
        features: geoFeatures
          .filter((feature) => {
            const lng = parseFloat(feature.properties.longitudeCentroid);
            const lat = parseFloat(feature.properties.latitudeCentroid);
            return !isNaN(lng) && !isNaN(lat);
          })
          .map((feature) => ({
            type: "Feature" as const,
            id: feature.properties.id.toString(),
            geometry: {
              type: "Point" as const,
              coordinates: [
                parseFloat(feature.properties.longitudeCentroid),
                parseFloat(feature.properties.latitudeCentroid),
              ],
            },
            properties: {
              name: feature.properties.name,
              id: feature.properties.id,
            },
          })),
      };
    },
    []
  );

  const calculateBounds = useCallback(
    (geometry: CoamAreaFeature["geometry"]): mapboxgl.LngLatBounds | null => {
      try {
        const bounds = new mapboxgl.LngLatBounds();

        if (geometry.type === "Polygon") {
          const coordinates = geometry.coordinates as number[][][];
          coordinates.forEach((ring) => {
            ring.forEach((coord) => {
              bounds.extend([coord[0], coord[1]]);
            });
          });
        } else if (geometry.type === "MultiPolygon") {
          const coordinates = geometry.coordinates as number[][][][];
          coordinates.forEach((polygon) => {
            polygon.forEach((ring) => {
              ring.forEach((coord) => {
                bounds.extend([coord[0], coord[1]]);
              });
            });
          });
        }

        if (bounds.isEmpty()) {
          return null;
        }

        return bounds;
      } catch (error) {
        console.error("Error calculating bounds:", error);
        return null;
      }
    },
    []
  );

  const detachEventHandlers = useCallback((mapInstance: mapboxgl.Map) => {
    const eventHandlers = eventHandlersRef.current;
    if (eventHandlers.handleMouseMove) {
      mapInstance.off("mousemove", LAYER_ID, eventHandlers.handleMouseMove);
    }
    if (eventHandlers.handleMouseLeave) {
      mapInstance.off("mouseleave", LAYER_ID, eventHandlers.handleMouseLeave);
    }
    if (eventHandlers.handleClick) {
      mapInstance.off("click", LAYER_ID, eventHandlers.handleClick);
      mapInstance.off("click", OUTLINE_LAYER_ID, eventHandlers.handleClick);
      mapInstance.off("click", LABEL_LAYER_ID, eventHandlers.handleClick);
    }
    eventHandlersRef.current = {
      handleMouseMove: null,
      handleMouseLeave: null,
      handleClick: null,
    };
  }, []);

  const attachEventHandlers = useCallback(
    (mapInstance: mapboxgl.Map, geoFeatures: CoamAreaFeature[]) => {
      detachEventHandlers(mapInstance);

      let hoveredPolygonId: string | null = null;

      const handleMouseMove = (e: mapboxgl.MapLayerMouseEvent) => {
        if (!e.features?.length || !e.features[0].id) return;

        mapInstance.getCanvas().style.cursor = "pointer";

        const currentId = e.features[0].id as string;

        if (hoveredPolygonId !== null && hoveredPolygonId !== currentId) {
          mapInstance.setFeatureState(
            { source: SOURCE_ID, id: hoveredPolygonId },
            { hover: false }
          );
        }

        hoveredPolygonId = currentId;
        hoveredPolygonIdRef.current = hoveredPolygonId;
        mapInstance.setFeatureState(
          { source: SOURCE_ID, id: hoveredPolygonId },
          { hover: true }
        );
      };

      const handleMouseLeave = () => {
        mapInstance.getCanvas().style.cursor = "";

        if (hoveredPolygonId !== null) {
          mapInstance.setFeatureState(
            { source: SOURCE_ID, id: hoveredPolygonId },
            { hover: false }
          );
          hoveredPolygonId = null;
          hoveredPolygonIdRef.current = null;
        }
      };

      const handleClick = (e: mapboxgl.MapLayerMouseEvent) => {
        if (!e.features?.length || !e.features[0].id) return;

        const clickedId = e.features[0].id as string;
        const wasPreviouslySelected = selectedPolygonIdRef.current !== null;

        if (selectedPolygonIdRef.current !== null) {
          mapInstance.setFeatureState(
            { source: SOURCE_ID, id: selectedPolygonIdRef.current },
            { selected: false }
          );
        }

        selectedPolygonIdRef.current = clickedId;
        mapInstance.setFeatureState(
          { source: SOURCE_ID, id: clickedId },
          { selected: true }
        );

        const selectedFeatureData = geoFeatures.find(
          (f) => f.properties.id === Number(clickedId)
        );

        if (selectedFeatureData) {
          setSelectedFeature(selectedFeatureData);

          const bounds = calculateBounds(selectedFeatureData.geometry);
          if (bounds) {
            mapInstance.fitBounds(bounds, {
              padding: PADDING,
              duration: ANIMATION_DURATION,
              maxZoom: 15,
            });
          } else {
            const featureProperties =
              selectedFeatureData.properties as CoamAreaFeature["properties"];

            if (
              featureProperties?.longitudeCentroid &&
              featureProperties?.latitudeCentroid
            ) {
              const lng = parseFloat(featureProperties.longitudeCentroid);
              const lat = parseFloat(featureProperties.latitudeCentroid);

              if (!isNaN(lng) && !isNaN(lat)) {
                const offset: [number, number] = wasPreviouslySelected
                  ? [-PANEL_WIDTH_OFFSET, 0]
                  : [0, 0];
                mapInstance.flyTo({
                  center: [lng, lat],
                  zoom: 10,
                  duration: ANIMATION_DURATION,
                  offset: offset,
                });
              }
            }
          }
        }
      };

      eventHandlersRef.current = {
        handleMouseMove,
        handleMouseLeave,
        handleClick,
      };

      mapInstance.on("mousemove", LAYER_ID, handleMouseMove);
      mapInstance.on("mouseleave", LAYER_ID, handleMouseLeave);
      mapInstance.on("click", LAYER_ID, handleClick);
      mapInstance.on("click", OUTLINE_LAYER_ID, handleClick);
      mapInstance.on("click", LABEL_LAYER_ID, handleClick);
    },
    [detachEventHandlers, setSelectedFeature, calculateBounds]
  );

  const initializeLayers = useCallback(
    (mapInstance: mapboxgl.Map, geoFeatures: CoamAreaFeature[]) => {
      try {
        const featureCollection = createFeatureCollection(geoFeatures);
        const labelFeatureCollection =
          createLabelFeatureCollection(geoFeatures);

        if (!mapInstance.getSource(SOURCE_ID)) {
          mapInstance.addSource(SOURCE_ID, {
            type: "geojson",
            data: featureCollection as unknown as Parameters<
              mapboxgl.GeoJSONSource["setData"]
            >[0],
            promoteId: "id",
          });
        } else {
          const source = mapInstance.getSource(
            SOURCE_ID
          ) as mapboxgl.GeoJSONSource;
          if (source?.setData) {
            source.setData(
              featureCollection as unknown as Parameters<
                mapboxgl.GeoJSONSource["setData"]
              >[0]
            );
          }
        }

        if (!mapInstance.getSource(LABEL_SOURCE_ID)) {
          mapInstance.addSource(LABEL_SOURCE_ID, {
            type: "geojson",
            data: labelFeatureCollection as unknown as Parameters<
              mapboxgl.GeoJSONSource["setData"]
            >[0],
            promoteId: "id",
          });
        } else {
          const labelSource = mapInstance.getSource(
            LABEL_SOURCE_ID
          ) as mapboxgl.GeoJSONSource;
          if (labelSource?.setData) {
            labelSource.setData(
              labelFeatureCollection as unknown as Parameters<
                mapboxgl.GeoJSONSource["setData"]
              >[0]
            );
          }
        }

        if (!mapInstance.getLayer(LAYER_ID)) {
          mapInstance.addLayer({
            id: LAYER_ID,
            type: "fill",
            source: SOURCE_ID,
            paint: {
              "fill-color": [
                "case",
                ["boolean", ["feature-state", "selected"], false],
                COLORS.selected.fill,
                ["boolean", ["feature-state", "hover"], false],
                COLORS.hover.fill,
                COLORS.default.fill,
              ],
              "fill-opacity": [
                "case",
                ["boolean", ["feature-state", "selected"], false],
                COLORS.selected.fillOpacity,
                ["boolean", ["feature-state", "hover"], false],
                COLORS.hover.fillOpacity,
                COLORS.default.fillOpacity,
              ],
            },
          });
        }

        if (!mapInstance.getLayer(OUTLINE_LAYER_ID)) {
          mapInstance.addLayer({
            id: OUTLINE_LAYER_ID,
            type: "line",
            source: SOURCE_ID,
            paint: {
              "line-color": [
                "case",
                ["boolean", ["feature-state", "selected"], false],
                COLORS.selected.stroke,
                ["boolean", ["feature-state", "hover"], false],
                COLORS.hover.stroke,
                COLORS.default.stroke,
              ],
              "line-width": [
                "case",
                ["boolean", ["feature-state", "selected"], false],
                2,
                ["boolean", ["feature-state", "hover"], false],
                1.5,
                1,
              ],
            },
          });
        }

        if (!mapInstance.getLayer(LABEL_LAYER_ID)) {
          mapInstance.addLayer({
            id: LABEL_LAYER_ID,
            type: "symbol",
            source: LABEL_SOURCE_ID,
            layout: {
              "text-field": ["get", "name"],
              "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
              "text-size": 12,
              "text-anchor": "center",
              "text-allow-overlap": false,
              "text-ignore-placement": false,
            },
            paint: {
              "text-color": theme.palette.text.primary,
              "text-halo-color": theme.palette.background.default,
              "text-halo-width": 1,
              "text-halo-blur": 1,
            },
          });
        }
      } catch (error) {
        console.error("Error initializing COAM area layers:", error);
      }
    },
    [
      COLORS,
      createFeatureCollection,
      createLabelFeatureCollection,
      theme.palette.text.primary,
      theme.palette.background.default,
    ]
  );

  useEffect(() => {
    if (!map || features.length === 0) return;

    const initializeLayersAndHandlers = () => {
      initializeLayers(map, features);
      attachEventHandlers(map, features);

      const savedSelectedId = selectedPolygonIdRef.current;
      const savedHoveredId = hoveredPolygonIdRef.current;

      if (savedSelectedId) {
        map.setFeatureState(
          { source: SOURCE_ID, id: savedSelectedId },
          { selected: true }
        );
      }
      if (savedHoveredId) {
        map.setFeatureState(
          { source: SOURCE_ID, id: savedHoveredId },
          { hover: true }
        );
      }
    };

    if (map.loaded()) {
      initializeLayersAndHandlers();
    }

    //map.on("load", initializeLayersAndHandlers);
    map.on("style.load", initializeLayersAndHandlers);

    return () => {
      //map.off("load", initializeLayersAndHandlers);
      map.off("style.load", initializeLayersAndHandlers);
      detachEventHandlers(map);
    };
  }, [
    map,
    features,
    initializeLayers,
    attachEventHandlers,
    detachEventHandlers,
  ]);

  useEffect(() => {
    if (
      !map ||
      selectedFeature !== null ||
      selectedPolygonIdRef.current === null
    )
      return;

    map.setFeatureState(
      { source: SOURCE_ID, id: selectedPolygonIdRef.current },
      { selected: false }
    );

    map.flyTo({
      center: UK_CENTER,
      zoom: INITIAL_ZOOM,
      duration: ANIMATION_DURATION,
    });

    selectedPolygonIdRef.current = null;

    hoveredPolygonIdRef.current = null;
  }, [map, selectedFeature]);
};
